import { Box, ButtonGroup, Drawer, Grid, IconButton, Tooltip, Zoom } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Create, ThumbUpAlt } from "@material-ui/icons";
import { useAccessControl, useAuth, useVerifyPermission } from "@airborne/react-admin";
import * as React from "react";
import { FC, Fragment } from "react";
import { Datagrid, Filter, FilterProps, ListProps, TextField, TextInput, useNotify, useRefresh } from "react-admin";
import { DetailButton } from "../../../Components/DetailButton";
import { ConfirmButton } from "../../../Components/ConfirmButton";
import { CustomDateField } from "../../../Components/CustomDateField";
import { DefaultCreatedDateSort, DefaultPerPageSize } from "../../../Constants/Constant";
import { useAppDataProvider } from "../../../dataprovider";
import { RequestStatus } from "../../../Enums/RequestStatus";
import { ExcelExport } from "../../../Helper/ExportExcel";
import { TabbedDatagridProps } from "../../../Models/TabbedDataGridProps";
import { SourceRequestOrder, WWVendorProcessListTitle } from "./Constant/Source";
import RequestOrderNotes from "./RequestOrderNotes";
import { RemarkTypes } from "../../../../src/Enums/RemarkTypes";
import PoolList from "../../../Components/PoolListComponent/PoolListComponent";

const useStyles = makeStyles(theme => ({
  drawerPaper: {
    width: "20%",
  },
}));
const TabbedDatagrid: FC<TabbedDatagridProps> = props => {
  const { remarkAddClick, ...rest } = props;
  const dataProvider = useAppDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const { user } = useAuth();
  const { canCreate, canEdit, canRead, hasAccess } = useAccessControl(SourceRequestOrder);

  const acceptButtonClick = record => e => {
    record.requestStatus = RequestStatus.WarrantyWorkscopeVendorProcess;
    dataProvider.updateRequestOrder(record).then(result => {
      notify(`Record updated`, "success", false, true);
      refresh();
    });
  };
  const remarkOnClick = record => e => {
    e.stopPropagation();
    remarkAddClick?.(record);
  };

  const AcceptReceivingButton = ({ record }) => {
    return (
      <ConfirmButton
        {...props}
        variant="text"
        style={{
          color: "#12824C",
        }}
        size="medium"
        click={acceptButtonClick(record)}
      >
        <Tooltip TransitionComponent={Zoom} title="Accept">
          <ThumbUpAlt />
        </Tooltip>
      </ConfirmButton>
    );
  };
  const RemarkButton = ({ record }) => {
    return (
      <IconButton style={{ width: 40 }} onClick={remarkOnClick(record)}>
        <Tooltip TransitionComponent={Zoom} title="Core Last Update">
          <Create />
        </Tooltip>
      </IconButton>
    );
  };
  const DetailsButton = ({ record }) => {
    return <DetailButton record={record} resource={"requestorders"} />;
  };
  const OptButtonGroup = ({ record }) => {
    return (
      <Box style={{ flex: 1, flexDirection: "row" }}>
        <ButtonGroup variant="text" size="small">
          <DetailsButton record={record} />
          {user?.isCustomer === false && canCreate && (
            <>
              <AcceptReceivingButton record={record} />
              <RemarkButton record={record} />
            </>
          )}
        </ButtonGroup>
      </Box>
    );
  };

  return (
    <Fragment>
      <Datagrid {...rest} optimized basePath={SourceRequestOrder}>
        <OptButtonGroup record={props} />
        <TextField source="unserviceable.pn" label="Pn" />
        <TextField source="unserviceable.sn" label="Sn" />
        <TextField source="componentDescription" label="Description" />
        <TextField source="ttWo" label="Wo" />
        <TextField source="ttRo" label="Ro" />
        <TextField source="refNo" label="Ref No" />
        <TextField source="customerPo" label="Customer Po" />
        <TextField source="customerCode" label="Customer" />
        <CustomDateField source="woCreatedDate" label="WO Created Date" showTime />
        <CustomDateField source="requestedDeliveryDate" label="Requested Delivery Date" showTime />
        {/* <CreatedByField label="Created By" link={false} />
          <CustomDateField source="createdDate" label="Created Date" showTime /> */}
        <TextField source="currentPbhNote" label="Current PBH Note" />
      </Datagrid>
    </Fragment>
  );
};

const WWVendorProcessList: FC<ListProps> = props => {
  useVerifyPermission("customroutes", "wwvendorprocess.read");
  const classes = useStyles();
  const [isMatchCreate, setIsMatchCreate] = React.useState(false);
  const [requestRemark, setRequestRemark] = React.useState<any>({});
  const ExportToExcel = data => {
    ExcelExport(
      [
        {
          header: "Pn",
          key: "Pn",
        },
        {
          header: "Sn",
          key: "Sn",
        },
        {
          header: "Description",
          key: "Description",
        },
        {
          header: "Wo",
          key: "Wo",
        },
        {
          header: "Ro",
          key: "Ro",
        },
        {
          header: "Ref No",
          key: "Ref No",
        },
        {
          header: "Customer Po",
          key: "Customer Po",
        },
        {
          header: "Customer",
          key: "Customer",
        },
        {
          header: "Wo Created Date",
          key: "Wo Created Date",
        },
        {
          header: "Requested Delivery Date",
          key: "Requested Delivery Date",
        },
        {
          header: "Created By",
          key: "Created By",
        },
        {
          header: "Created Date",
          key: "Created Date",
        },
        {
          header: "Current PBH Note",
          key: "Current PBH Note",
        },
      ],
      data.map(v => [
        v?.unserviceable?.pn,
        v?.unserviceable?.sn,
        v.componentDescription,
        v.woCount,
        v.roCount,
        v.refNo,
        v.customerPo,
        v.customerCode,
        v.woCreatedDate,
        v.requestedDeliveryDate,
        v.createdBy,
        v.createdDate,
        v.currentPbhNote,
      ]),
      "KKM"
    );
  };
  const remarkAddClick = record => {
    setIsMatchCreate(true);
    setRequestRemark(record);
  };
  const handleClose = () => {
    setIsMatchCreate(false);
  };

  const VendorProcessFilter: FC<Omit<FilterProps, "children">> = props => (
    <Filter {...props}>
      <TextInput label="Pn" source="pn__contains" alwaysOn />
      <TextInput label="Sn" source="sn__contains" alwaysOn />
      <TextInput label="Description" source="componentDescription__contains" alwaysOn />
      <TextInput label="WO" source="ttWo__eq" alwaysOn />
      <TextInput label="RO" source="ttRo__eq" alwaysOn />
      <TextInput label="Ref No" source="refNo__contains" alwaysOn />
      <TextInput label="Customer PO" source="customerPo__contains" alwaysOn />
      <TextInput label="Customer" source="customerCode__contains" alwaysOn />
    </Filter>
  );

  return (
    <Grid container>
      <Grid item xs={isMatchCreate ? 9 : 12}>
        <PoolList
          {...props}
          hasCreate={false}
          exporter={ExportToExcel}
          bulkActionButtons={false}
          resource={SourceRequestOrder}
          basePath={SourceRequestOrder}
          title={WWVendorProcessListTitle}
          filterDefaultValues={{
            isUnsvcInfo__eq: true,
            isWhInfo__eq: true,
            [`requestStatus =  ${RequestStatus.CapabilityWarrantyFailed} || requestStatus = ${RequestStatus.ToBeKKm}`]:
              "test",
          }}
          sort={DefaultCreatedDateSort}
          perPage={DefaultPerPageSize}
          filters={<VendorProcessFilter />}
        >
          <TabbedDatagrid remarkAddClick={remarkAddClick} />
        </PoolList>
      </Grid>
      {isMatchCreate && (
        <Grid item xs={3}>
          <Drawer
            classes={{
              paper: classes.drawerPaper,
            }}
            variant="persistent"
            open={isMatchCreate}
            anchor="right"
            onClose={handleClose}
          >
            {isMatchCreate ? (
              <RequestOrderNotes
                onCancel={handleClose}
                request={requestRemark}
                resource="requestremarks"
                basePath="requestremarks"
                remarkType={RemarkTypes.PbhNote}
              />
            ) : null}
          </Drawer>
        </Grid>
      )}
    </Grid>
  );
};

export default WWVendorProcessList;
